import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";
import { Eye, Loader2, RefreshCw, ArrowRightLeft } from "lucide-react";

const ESTADO_BADGE = {
  abierto: "bg-blue-100 text-blue-700",
  en_progreso: "bg-amber-100 text-amber-700",
  pendiente: "bg-purple-100 text-purple-700",
  resuelto: "bg-emerald-100 text-emerald-700",
  cerrado: "bg-slate-200 text-slate-600",
};

const fmt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("es-DO", { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso; }
};

export default function TicketsReasignadosTab({ modulo = null }) {
  const { getAuthHeaders } = useAuth();
  // Cabeceras con módulo (Hallazgos Auditoria) cuando aplica
  const authH = () => {
    const base = (getAuthHeaders)();
    return modulo ? { ...base, headers: { ...base.headers, "X-Modulo": modulo } } : base;
  };
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [verTicket, setVerTicket] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/tickets/reasignados`, authH());
      setTickets(res.data || []);
    } catch (e) { toast.error("Error al cargar tickets reasignados"); }
    finally { setLoading(false); }
  }, [getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  return (
    <Card data-testid="tickets-reasignados-tab">
      <CardContent className="pt-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-[#0C314C] dark:text-blue-400" /> Tickets Reasignados
            </h2>
            <p className="text-sm text-slate-500">Seguimiento de solo lectura de los tickets que se han reasignado a otros usuarios</p>
          </div>
          <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="reasignados-refresh-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
        </div>
        {tickets.length === 0 ? (
          <p className="text-sm text-slate-500 py-8 text-center" data-testid="reasignados-vacio">
            No hay tickets reasignados para mostrar.
          </p>
        ) : (
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
            <div className="overflow-x-auto">
              <Table data-testid="reasignados-table">
                <TableHeader>
                  <TableRow className="bg-[#0C314C] hover:bg-[#0C314C]">
                    <TableHead className="text-white font-semibold">#</TableHead>
                    <TableHead className="text-white font-semibold">Título</TableHead>
                    <TableHead className="text-white font-semibold">Reasignado por</TableHead>
                    <TableHead className="text-white font-semibold">De → A</TableHead>
                    <TableHead className="text-white font-semibold">Motivo</TableHead>
                    <TableHead className="text-white font-semibold">Fecha reasignación</TableHead>
                    <TableHead className="text-white font-semibold">Asignado actual</TableHead>
                    <TableHead className="text-white font-semibold">Estado</TableHead>
                    <TableHead className="text-white font-semibold text-right">Ver</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tickets.map((t, idx) => {
                    const r = t.ultima_reasignacion || {};
                    return (
                      <TableRow key={t.id}
                        className={`${idx % 2 === 1 ? "bg-slate-50/70 dark:bg-slate-800/40" : ""} hover:bg-blue-50/50 dark:hover:bg-blue-950/30`}
                        data-testid={`reasignado-row-${t.id}`}>
                        <TableCell className="font-medium">#{t.numero}</TableCell>
                        <TableCell className="max-w-[220px] truncate font-medium" title={t.titulo}>{t.titulo}</TableCell>
                        <TableCell>{r.reasignado_por_nombre || "—"}</TableCell>
                        <TableCell className="whitespace-nowrap text-sm">
                          {r.de_usuario_nombre || "Sin asignar"} <span className="text-slate-400">→</span> {r.a_usuario_nombre || "—"}
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate" title={r.motivo}>{r.motivo || "—"}</TableCell>
                        <TableCell className="whitespace-nowrap">{fmt(r.fecha)}</TableCell>
                        <TableCell>{t.asignado_a_nombre || "Sin asignar"}</TableCell>
                        <TableCell>
                          <Badge className={`${ESTADO_BADGE[t.estado] || "bg-slate-200 text-slate-600"} capitalize`} data-testid={`reasignado-estado-${t.id}`}>
                            {(t.estado || "").replace("_", " ")}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-500 hover:text-[#0C314C]"
                            onClick={() => setVerTicket(t)} data-testid={`reasignado-ver-${t.id}`}>
                            <Eye className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </CardContent>

      <Dialog open={!!verTicket} onOpenChange={(v) => !v && setVerTicket(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="reasignado-ver-dialog">
          {verTicket && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  Ticket #{verTicket.numero} — {verTicket.titulo}
                  <Badge variant="outline" className="text-xs">Solo lectura</Badge>
                </DialogTitle>
                <p className="text-xs text-slate-500">Vista de seguimiento del ticket reasignado. No permite realizar cambios.</p>
              </DialogHeader>
              <div className="space-y-4 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <div><span className="text-slate-500">Estado:</span> <Badge className={`${ESTADO_BADGE[verTicket.estado] || "bg-slate-200 text-slate-600"} capitalize ml-1`}>{(verTicket.estado || "").replace("_", " ")}</Badge></div>
                  <div><span className="text-slate-500">Prioridad:</span> <span className="font-medium capitalize">{verTicket.prioridad || "—"}</span></div>
                  <div><span className="text-slate-500">Solicitante:</span> <span className="font-medium">{verTicket.solicitante_nombre || "—"}</span></div>
                  <div><span className="text-slate-500">Asignado actual:</span> <span className="font-medium">{verTicket.asignado_a_nombre || "Sin asignar"}</span></div>
                  <div><span className="text-slate-500">Creado:</span> {fmt(verTicket.fecha_creacion)}</div>
                  <div><span className="text-slate-500">Actualizado:</span> {fmt(verTicket.fecha_actualizacion)}</div>
                </div>
                <div>
                  <p className="text-slate-500 mb-1">Descripción</p>
                  <div className="rounded-lg bg-slate-50 dark:bg-slate-800 p-3 whitespace-pre-wrap">{verTicket.descripcion || "—"}</div>
                </div>
                <div>
                  <p className="text-slate-500 mb-1">Historial de reasignaciones ({(verTicket.historial_reasignaciones || []).length})</p>
                  <div className="space-y-2">
                    {[...(verTicket.historial_reasignaciones || [])].reverse().map((h) => (
                      <div key={h.id} className="rounded-lg border border-slate-200 dark:border-slate-700 p-2">
                        <p className="font-medium">{h.de_usuario_nombre || "Sin asignar"} → {h.a_usuario_nombre}</p>
                        <p className="text-xs text-slate-500">Por {h.reasignado_por_nombre} · {fmt(h.fecha)}</p>
                        <p className="text-xs mt-1"><span className="text-slate-500">Motivo:</span> {h.motivo}</p>
                      </div>
                    ))}
                  </div>
                </div>
                {(verTicket.comentarios || []).length > 0 && (
                  <div>
                    <p className="text-slate-500 mb-1">Comentarios ({verTicket.comentarios.length})</p>
                    <div className="space-y-2 max-h-52 overflow-y-auto">
                      {[...verTicket.comentarios].reverse().map((c) => (
                        <div key={c.id} className="rounded-lg bg-slate-50 dark:bg-slate-800 p-2">
                          <p className="text-xs text-slate-500">{c.autor} · {fmt(c.fecha)}</p>
                          <p className="whitespace-pre-wrap">{c.texto}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setVerTicket(null)} data-testid="reasignado-cerrar-btn">Cerrar</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
